import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Eye, Download, Users, Code, FileText, HelpCircle, UserPlus, Key, Trash2, Wrench } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import ApiService from '../services/api';

const AdminDashboard = () => {
  const { scripts, articles, faqs, tools, users, analytics, deleteScript, deleteArticle, deleteFaq, deleteTool } = useData();
  const { user: currentUser, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('scripts');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Filter content based on user role (API uses author_id; some UI used authorId)
  const owns = (item) => {
    const aid = item.author_id ?? item.authorId;
    return String(aid) === String(currentUser.id);
  };
  const userScripts = isAdmin ? scripts : scripts.filter(owns);
  const userArticles = isAdmin ? articles : articles.filter(owns);
  const userFaqs = isAdmin ? faqs : faqs.filter(owns);
  const userTools = isAdmin ? tools : tools.filter(owns);

  const stats = [
    { label: 'Total Scripts', value: userScripts.length, icon: Code, color: '#28a745' },
    { label: 'Total Articles', value: userArticles.length, icon: FileText, color: '#17a2b8' },
    { label: 'Total FAQ', value: userFaqs.length, icon: HelpCircle, color: '#ffc107' },
    { label: 'Total Tools', value: userTools.length, icon: Wrench, color: '#e83e8c' },
    ...(isAdmin ? [{ label: 'Total Users', value: users.length, icon: Users, color: '#6f42c1' }] : []),
    { label: 'Total Downloads', value: analytics.totalDownloads, icon: Download, color: '#28a745' },
    { label: 'Total Visits', value: analytics.totalVisits, icon: Users, color: '#17a2b8' },
    { label: 'Page Views', value: Object.values(analytics.pageViews).reduce((a, b) => a + b, 0), icon: Eye, color: '#ffc107' }
  ];

  const topScripts = scripts
    .map(script => ({
      ...script,
      downloads: analytics.scriptDownloads[script.id] || 0
    }))
    .sort((a, b) => b.downloads - a.downloads)
    .slice(0, 10);

  const topPages = Object.entries(analytics.pageViews)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10);

  const handleDelete = async (type, id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }
    try {
      if (type === 'script') await deleteScript(id);
      else if (type === 'article') await deleteArticle(id);
      else if (type === 'faq') await deleteFaq(id);
      else if (type === 'tool') await deleteTool(id);
    } catch (error) {
      alert(`Failed to delete ${type}: ${error.message}`);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    try {
      await ApiService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setPasswordSuccess('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setShowPasswordChange(false), 2000);
    } catch (error) {
      setPasswordError(error.message || 'Failed to change password');
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1 className="page-title">Admin Dashboard</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Link to="/admin/editor?type=script" className="btn btn-primary">
            <Plus size={16} />
            Add Script
          </Link>
          <Link to="/admin/editor?type=article" className="btn btn-secondary">
            <Plus size={16} />
            Add Article
          </Link>
          <Link to="/admin/editor?type=faq" className="btn btn-success">
            <Plus size={16} />
            Add FAQ
          </Link>
          <Link to="/admin/editor?type=tool" className="btn btn-info" style={{ background: '#17a2b8', color: 'white', borderColor: '#17a2b8' }}>
            <Plus size={16} />
            Add Tool
          </Link>
          {isAdmin && (
            <Link to="/admin/users" className="btn btn-success">
              <UserPlus size={16} />
              Manage Users
            </Link>
          )}
          <button onClick={() => setShowPasswordChange(!showPasswordChange)} className="btn btn-warning">
            <Key size={16} />
            Change Password
          </button>
        </div>
      </div>

      {showPasswordChange && (
        <div className="password-change-section" style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
          <h3>Change Password</h3>
          {passwordError && <div style={{ color: '#dc3545', marginBottom: '10px' }}>{passwordError}</div>}
          {passwordSuccess && <div style={{ color: '#28a745', marginBottom: '10px' }}>{passwordSuccess}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
            <div>
              <label>Current Password</label>
              <input
                type="password"
                className="form-input"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
              />
            </div>
            <div>
              <label>New Password</label>
              <input
                type="password"
                className="form-input"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
              />
            </div>
            <div>
              <label>Confirm Password</label>
              <input
                type="password"
                className="form-input"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
              />
            </div>
            <button onClick={handlePasswordChange} className="btn btn-primary">
              Update
            </button>
          </div>
        </div>
      )}

      <div className="stats-grid">
        {stats.map(stat => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="stat-card">
              <Icon size={32} color={stat.color} />
              <div className="stat-number">{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '40px' }}>
        <div className="data-table">
          <h3 style={{ padding: '20px', margin: 0, background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
            Top Downloaded Scripts
          </h3>
          <table className="table">
            <thead>
              <tr>
                <th>Script Title</th>
                <th>Category</th>
                <th>Downloads</th>
              </tr>
            </thead>
            <tbody>
              {topScripts.map(script => (
                <tr key={script.id}>
                  <td>
                    <Link to={`/script/${script.id}`} style={{ color: '#28a745', textDecoration: 'none' }}>
                      {script.title}
                    </Link>
                  </td>
                  <td>{script.category}</td>
                  <td>{script.downloads}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="data-table">
          <h3 style={{ padding: '20px', margin: 0, background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
            Most Visited Pages
          </h3>
          <table className="table">
            <thead>
              <tr>
                <th>Page</th>
                <th>Views</th>
              </tr>
            </thead>
            <tbody>
              {topPages.map(([page, views]) => (
                <tr key={page}>
                  <td>{page}</td>
                  <td>{views}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="category-tabs">
        <button 
          className={`tab-button ${activeTab === 'scripts' ? 'active' : ''}`}
          onClick={() => setActiveTab('scripts')}
        >
          <Code size={18} />
          Scripts ({userScripts.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'articles' ? 'active' : ''}`}
          onClick={() => setActiveTab('articles')}
        >
          <FileText size={18} />
          Articles ({userArticles.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'faqs' ? 'active' : ''}`}
          onClick={() => setActiveTab('faqs')}
        >
          <HelpCircle size={18} />
          FAQ ({userFaqs.length})
        </button>
        <button 
          className={`tab-button ${activeTab === 'tools' ? 'active' : ''}`}
          onClick={() => setActiveTab('tools')}
        >
          <Wrench size={18} />
          Tools ({userTools.length})
        </button>
      </div>

      <div className="data-table">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
          <h3 style={{ margin: 0 }}>{activeTab === 'scripts' ? 'All Scripts' : activeTab === 'articles' ? 'All Articles' : activeTab === 'tools' ? 'All Tools' : 'All FAQ'}</h3>
          <Link to={`/admin/editor?type=${activeTab === 'tools' ? 'tool' : activeTab.slice(0, -1)}`} className="btn btn-primary">
            <Plus size={16} />
            Add {activeTab === 'scripts' ? 'Script' : activeTab === 'articles' ? 'Article' : activeTab === 'tools' ? 'Tool' : 'FAQ'}
          </Link>
        </div>
        {activeTab === 'scripts' ? (
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Subcategory</th>
                <th>Created</th>
                <th>Downloads</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userScripts.map(script => (
                <tr key={script.id}>
                  <td>
                    <Link to={`/script/${script.id}`} style={{ color: '#28a745', textDecoration: 'none' }}>
                      {script.title}
                    </Link>
                  </td>
                  <td>{script.category}</td>
                  <td>{script.subcategory}</td>
                  <td>{new Date(script.created_at).toLocaleDateString()}</td>
                  <td>{analytics.scriptDownloads[script.id] || 0}</td>
                  <td>
                    <Link to={`/admin/editor/${script.id}?type=script`} className="btn btn-secondary" style={{ marginRight: '10px' }}>
                      <Edit size={14} />
                      Edit
                    </Link>
                    <Link to={`/script/${script.id}`} className="btn btn-primary" style={{ marginRight: '10px' }}>
                      <Eye size={14} />
                      View
                    </Link>
                    <button onClick={() => handleDelete('script', script.id, script.title)} className="btn" style={{ background: '#dc3545', color: 'white', border: 'none' }}>
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : activeTab === 'articles' ? (
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Author</th>
                <th>Read Time</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userArticles.map(article => (
                <tr key={article.id}>
                  <td>
                    <Link to={`/article/${article.id}`} style={{ color: '#28a745', textDecoration: 'none' }}>
                      {article.title}
                    </Link>
                  </td>
                  <td>{article.category}</td>
                  <td>{article.author}</td>
                  <td>{article.readTime}</td>
                  <td>{new Date(article.created_at).toLocaleDateString()}</td>
                  <td>
                    <Link to={`/admin/editor/${article.id}?type=article`} className="btn btn-secondary" style={{ marginRight: '10px' }}>
                      <Edit size={14} />
                      Edit
                    </Link>
                    <Link to={`/article/${article.id}`} className="btn btn-primary" style={{ marginRight: '10px' }}>
                      <Eye size={14} />
                      View
                    </Link>
                    <button onClick={() => handleDelete('article', article.id, article.title)} className="btn" style={{ background: '#dc3545', color: 'white', border: 'none' }}>
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : activeTab === 'faqs' ? (
          <table className="table">
            <thead>
              <tr>
                <th>Question</th>
                <th>Category</th>
                <th>Difficulty</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userFaqs.map(faq => (
                <tr key={faq.id}>
                  <td>
                    <div style={{ color: '#28a745', textDecoration: 'none', fontWeight: '500' }}>
                      {faq.question}
                    </div>
                  </td>
                  <td>{faq.category}</td>
                  <td>
                    <span className={`difficulty-badge ${faq.difficulty?.toLowerCase()}`}>
                      {faq.difficulty}
                    </span>
                  </td>
                  <td>{new Date(faq.created_at).toLocaleDateString()}</td>
                  <td>
                    <Link to={`/admin/editor/${faq.id}?type=faq`} className="btn btn-secondary" style={{ marginRight: '10px' }}>
                      <Edit size={14} />
                      Edit
                    </Link>
                    <button onClick={() => handleDelete('faq', faq.id, faq.question)} className="btn" style={{ background: '#dc3545', color: 'white', border: 'none' }}>
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : activeTab === 'tools' ? (
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Version</th>
                <th>Platform</th>
                <th>File</th>
                <th>Downloads</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userTools.map(tool => (
                <tr key={tool.id}>
                  <td>
                    <Link to={`/tool/${tool.id}`} style={{ color: '#17a2b8', textDecoration: 'none' }}>
                      {tool.title}
                    </Link>
                  </td>
                  <td>{tool.category}</td>
                  <td>{tool.version || '-'}</td>
                  <td>{tool.platform || 'Windows'}</td>
                  <td>
                    {tool.file_name ? (
                      <span style={{ color: '#28a745', fontSize: '0.85rem' }}>
                        ✅ {tool.file_name}
                        {tool.file_size && <span style={{ color: '#6c757d' }}> ({tool.file_size})</span>}
                      </span>
                    ) : (
                      <span style={{ color: '#dc3545', fontSize: '0.85rem' }}>No file</span>
                    )}
                  </td>
                  <td>{tool.downloads || 0}</td>
                  <td>{new Date(tool.created_at).toLocaleDateString()}</td>
                  <td>
                    <Link to={`/admin/editor/${tool.id}?type=tool`} className="btn btn-secondary" style={{ marginRight: '10px' }}>
                      <Edit size={14} />
                      Edit
                    </Link>
                    <Link to={`/tool/${tool.id}`} className="btn btn-primary" style={{ marginRight: '10px' }}>
                      <Eye size={14} />
                      View
                    </Link>
                    <button onClick={() => handleDelete('tool', tool.id, tool.title)} className="btn" style={{ background: '#dc3545', color: 'white', border: 'none' }}>
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </div>
  );
};

export default AdminDashboard;